import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Mail, Send, Eye, X, AlertCircle } from "lucide-react";

// Returns next workday (skips Sat/Sun + provided holiday set), starting from `start` Date.
function nextWorkday(start, holidays) {
  const d = new Date(start);
  for (let i = 0; i < 14; i++) {
    const iso = d.toISOString().slice(0, 10);
    const dow = d.getDay(); // 0=Sun..6=Sat
    if (dow !== 0 && dow !== 6 && !holidays.has(iso)) return iso;
    d.setDate(d.getDate() + 1);
  }
  return start.toISOString().slice(0, 10);
}

const RESULT_COLORS = {
  fresh:     { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "first send" },
  changed:   { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "changed" },
  fyi:       { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", label: "FYI · notes only" },
  unchanged: { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", label: "unchanged" },
  "no-work": { bg: "bg-gray-50", text: "text-gray-400", border: "border-gray-100", label: "no work" },
  "opted-out": { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", label: "opted out" },
  "no-email": { bg: "bg-red-50", text: "text-red-600", border: "border-red-200", label: "no email" },
};

export default function EmailDispatchDialog({ open, onClose }) {
  const [holidays, setHolidays] = useState(new Set());
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("full");
  const [includeInstallers, setIncludeInstallers] = useState(true);
  const [includeForemen, setIncludeForemen] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [previewing, setPreviewing] = useState(null); // { recipient_id, html, subject } | null

  useEffect(() => {
    if (!open) return;
    api.get("/holidays").then((r) => {
      setHolidays(new Set((r.data || []).map((h) => h.date)));
    }).catch(() => {});
  }, [open]);

  // Default the date to next workday once holidays load
  useEffect(() => {
    if (!open) return;
    if (date) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(nextWorkday(tomorrow, holidays));
  }, [open, holidays, date]);

  const isHoliday = useMemo(() => date && holidays.has(date), [date, holidays]);
  const isWeekend = useMemo(() => {
    if (!date) return false;
    const d = new Date(date + "T12:00");
    return d.getDay() === 0 || d.getDay() === 6;
  }, [date]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/dispatch/email", {
        target_date: date,
        mode,
        include_installers: includeInstallers,
        include_foremen: includeForemen,
      });
      setResults(r.data);
      const summary = `${r.data.queued} queued · ${r.data.unchanged} unchanged · ${r.data.skipped} skipped`;
      toast.success(`Mock-sent for ${r.data.target_date} · ${summary}`);
    } catch {
      toast.error("Email dispatch failed");
    } finally {
      setRunning(false);
    }
  };

  const openPreview = async (recipientId, kind) => {
    try {
      const r = await api.get("/dispatch/email/preview", {
        params: { installer_id: recipientId, target_date: date, kind },
      });
      setPreviewing({
        recipient_id: recipientId,
        html: r.data.html,
        subject: r.data.payload?.subject || "(no scheduled work)",
        empty: r.data.empty,
      });
    } catch {
      toast.error("Could not load preview");
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="email-dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" /> Email Dispatch <span className="text-xs font-normal text-gray-500">· mocked (logs only — Resend not wired yet)</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 block mb-0.5">Target date</label>
              <Input
                data-testid="email-dispatch-date"
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setResults(null); }}
                className="h-9"
              />
              {(isHoliday || isWeekend) && (
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {isHoliday ? "Company holiday" : "Weekend"} — installers usually aren't scheduled
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-0.5">Mode</label>
              <div className="inline-flex border border-gray-300 rounded-md overflow-hidden" role="group">
                <button
                  type="button"
                  onClick={() => setMode("full")}
                  data-testid="email-mode-full"
                  className={`px-3 h-9 text-xs ${mode === "full" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
                >Full dispatch</button>
                <button
                  type="button"
                  onClick={() => setMode("changes")}
                  data-testid="email-mode-changes"
                  className={`px-3 h-9 text-xs border-l border-gray-300 ${mode === "changes" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
                >Changes only</button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                {mode === "full" ? "Sends to every scheduled recipient." : "Sends only when card differs from last log; notes-only edits become FYI."}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={includeInstallers} onChange={(e) => setIncludeInstallers(e.target.checked)} data-testid="email-include-installers" />
              Installers
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={includeForemen} onChange={(e) => setIncludeForemen(e.target.checked)} data-testid="email-include-foremen" />
              Foremen (job roll-up)
            </label>
          </div>

          {/* Results */}
          {results && (
            <div className="mt-3 border border-gray-200 rounded-md overflow-hidden" data-testid="email-dispatch-results">
              <header className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Results · {results.target_date}</span>
                <div className="flex gap-2 text-[11px]">
                  <span className="text-blue-700"><b>{results.queued}</b> queued</span>
                  {results.changed > 0 && <span className="text-amber-700"><b>{results.changed}</b> changed</span>}
                  {results.fyi_only > 0 && <span className="text-purple-700"><b>{results.fyi_only}</b> FYI</span>}
                  <span className="text-gray-500"><b>{results.unchanged}</b> unchanged</span>
                  <span className="text-gray-400"><b>{results.skipped}</b> skipped</span>
                </div>
              </header>
              <ul className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                {results.results.map((r, idx) => {
                  const c = RESULT_COLORS[r.result] || RESULT_COLORS.unchanged;
                  return (
                    <li key={idx} className="px-3 py-1.5 flex items-center gap-2 text-xs hover:bg-gray-50">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${c.bg} ${c.text} border ${c.border} shrink-0`}>{c.label}</span>
                      <span className={`flex-1 truncate ${r.kind === "foreman" ? "font-medium text-emerald-800" : "text-gray-800"}`}>
                        {r.recipient}{r.kind === "foreman" ? " · roll-up" : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Recipient preview launcher (uses a quick installer search to pick a row) */}
          <div className="mt-3 text-xs text-gray-500">
            Need to inspect what gets sent? Use the <b>Email Log</b> in Settings → Data to view rendered emails.
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={onClose} disabled={running}>Close</Button>
            <Button
              onClick={run}
              disabled={running || !date}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="email-dispatch-run-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {running ? "Sending…" : `Mock-send (${mode})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email preview modal */}
      {previewing && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreviewing(null)}
        >
          <div className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-6xl h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-400">Subject</p>
                <p className="text-sm font-medium text-gray-900 truncate">{previewing.subject}</p>
              </div>
              <button onClick={() => setPreviewing(null)} className="text-gray-400 hover:text-gray-700 ml-2"><X className="w-4 h-4" /></button>
            </header>
            <iframe
              title="email-preview"
              srcDoc={previewing.html}
              sandbox=""
              className="flex-1 w-full bg-white"
              data-testid="email-preview-iframe"
            />
          </div>
        </div>
      )}
    </>
  );
}
